import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { registerLocale } from 'react-datepicker'
import moment from 'moment'
import de from 'date-fns/locale/de'
import { Card, Row, Col } from 'react-bootstrap'

import KeyFactsForm from '../../Simulation/SimulationForm/KeyFactsFormPrisma'

import Input from '../../Shared/Input'
import DropDown from 'components/Shared/DropDown'
import { useHistory } from 'react-router-dom'
import Textarea from 'components/Shared/Textarea'
import FormButtons from './FormButtons'
import minusIcon from 'images/icons/minus-circle.svg'
import useHasPermission, { Permissions } from 'hooks/useHasPermission'
import useCustomTranslation from 'hooks/useCustomTranslation'
import Switcher from './Switcher'
import { specialAdCategories } from 'components/Simulation/constants'
import PortalModal from 'components/Shared/PortalModal'
import SpecialAdConfirmModal from './SpecialAdConfirmModal'

registerLocale('de', de)

const formatData = (name, description, fields, tz, t, specialAdCategory = [], isSpecialTab = false) => {
  let ERROR = false
  let errMsg = t('common.errors.fillRequired')
  const resp = fields.map((field) => {
    let { date, category } = field
    if (!date || !name || !category) ERROR = true
    if (!description || !description.trim()) ERROR = true
    // Validation for special_ad_category when on special tab
    if (isSpecialTab && (!Array.isArray(specialAdCategory) || specialAdCategory.length === 0 || !specialAdCategory[0])) {
      ERROR = true
    }

    return {
      name,
      description,
      date,
      category,
      tz,
      special_ad_category: specialAdCategory || [],
      status: 'RUNNING',
    }
  })

  if (ERROR) return { error: true, errMsg, resp: null }
  return { error: false, resp }
}

/**
 * Set simulation form with datapicker.
 * @Tags( simulation, api )
 * @Inputs( eventName, Date, Time, Venue, Hall, Capacity, Price, Artists, Tracks, Categories )
 * @NpmPackages( react-datepicker, date-fns )
 */
const EventEditorFormPrisma = ({
  event,
  category,
  postUpdatedEvent,
  renderFormButtons,
  onFormChanged = null,
  onCategoryChanged = null,
  activeTab,
  onTabChanged,
  isPublished,
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [fieldsChanged, setFieldsChanged] = useState(false)
  const firstRender = useRef(true)
  const hasLookoutAccess = useHasPermission(Permissions.lookout)
  const cancelRedirectUrl = `/${hasLookoutAccess && event.category !== 'product' ? 'events' : 'campaigns'}?drawer-open=true&event-id=${event.id}`
  const [error, setError] = useState('')
  const [tz, setTz] = useState('')
  const { getTranslation } = useCustomTranslation(event)
  const [simName, setSimName] = useState(event.title)
  const [description, setDescription] = useState(event.description)
  const isSpecialTab = activeTab === 'special'
  const [specialAdCategory, setSpecialAdCategory] = useState(
    isSpecialTab && Array.isArray(event.special_ad_category) && event.special_ad_category.length > 0
      ? event.special_ad_category[0]
      : ''
  )
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [fields, setFields] = useState([
    {
      date: event.date || '',
    },
  ])

  useEffect(() => {
    // if onFormChanged is passed, call it with the current form data
    if (!onFormChanged) return

    const fieldsWithCategory = fields.map((eachField) => {
      return {
        category,
        ...eachField,
      }
    })

    const specialAdCategoryValue = isSpecialTab && specialAdCategory ? [specialAdCategory] : []
    const { error, resp: formPayload } = formatData(simName, description, fieldsWithCategory, tz, t, specialAdCategoryValue, isSpecialTab)

    onFormChanged(formPayload?.[0], !error)
  }, [fields, category, simName, description, onFormChanged, t, tz, isSpecialTab, specialAdCategory])

  const removeField = (index) => {
    setFields(fields.filter((item, i) => index !== i))
  }

  const updateInput = (e, idx) => {
    const { name, value } = e.target

    if (name === 'name') {
      setSimName(value)
    } else if (name === 'description') {
      setDescription(value)
    } else {
      const currentFields = [...fields]
      currentFields[idx][name] = value
      setFields(currentFields)
    }
  }

  const updateDate = (date, idx) => {
    const formattedDate = date ? moment(date).format('yyyy-MM-DD') : ''
    const formattedTime = date ? moment(date).format('HH:mm') : ''
    const formattedTimezone = date ? Intl.DateTimeFormat().resolvedOptions().timeZone : ''

    const updatedFields = [...fields]
    updatedFields[idx].time = formattedTime
    updatedFields[idx].date = formattedDate

    setFields(updatedFields)
    setTz(formattedTimezone)
  }

  const handleSubmit = () => {
    setLoading(true)
    setError('')

    const fieldsWithCategory = fields.map((eachField) => {
      return {
        category,
        ...eachField,
      }
    })

    const specialAdCategoryValue = isSpecialTab && specialAdCategory ? [specialAdCategory] : []
    const {
      error,
      errMsg,
      resp: formPayload,
    } = formatData(simName, description, fieldsWithCategory, tz, t, specialAdCategoryValue, isSpecialTab)
    if (error) {
      setError(errMsg)
      setLoading(false)
      return
    }
    const sendFormAsync = async () => {
      try {
        await postUpdatedEvent(formPayload[0])
      } catch (error) {
        setError(error.message)
      }
    }
    sendFormAsync()
    setLoading(false)
  }

  const submitForm = (e) => {
    e.preventDefault()

    // For special tab, check if specialAdCategory changed before submitting
    if (isSpecialTab) {
      const eventSpecialAdCategory = Array.isArray(event.special_ad_category) && event.special_ad_category.length > 0
        ? event.special_ad_category[0]
        : ''
      
      if (specialAdCategory === eventSpecialAdCategory) {
        handleSubmit()
        return
      }

      setShowConfirmModal(true)
      return
    }

    handleSubmit()
  }

  const onCancelClick = () => {
    if (fieldsChanged) {
      return
    }
    history.replace(cancelRedirectUrl)
  }

  useEffect(() => {
    // Determine if fields were changed in any way while skipping the page's first render.
    if (firstRender.current) {
      firstRender.current = false
      return
    }

    setFieldsChanged(true)
  }, [fields, category, simName])

  useEffect(() => {
    if (!mounted) {
      // const formatedDate = event.date ? moment(event.date).format('DD.MM.YYYY') : ''
      // const formatedTime = event.start ? moment(event.start).format('HH:mm') : ''
      const formatedTimezone = event.date ? Intl.DateTimeFormat().resolvedOptions().timeZone : ''

      setTz(formatedTimezone)
      setMounted(true)
    }
  }, [event.date, mounted, setMounted, setTz])

  return (
    <>
      <form className="simulation-form" onSubmit={(e) => e.preventDefault()} noValidate>
        <>
          <div className="simulation-form__section simulation-form__key-facts">
            <Card className="card card-default mb-0">
              <Card.Body>
                <div className="simulation-form_header-wrap">
                  <div className="requiredInputBox simulation-form_sim-name-wrap">
                    <Input
                      placeholder={getTranslation('Simulation.form.eventName')}
                      separatePlaceholder={true}
                      value={simName}
                      name="name"
                      onChange={(e) => updateInput(e)}
                      required
                    />
                  </div>

                  <div className="simulation-form__tab-category-wrap">
                    <Switcher 
                      selectedOption={activeTab} 
                      setSelectedOption={onTabChanged} 
                      onCategoryChanged={onCategoryChanged} 
                      isPublished={isPublished} 
                      specialAdCategory={isSpecialTab ? specialAdCategory : null}
                    />
                  
                    {isSpecialTab && (
                      <DropDown
                        onChange={setSpecialAdCategory}
                        value={specialAdCategory}
                        options={specialAdCategories.map(category => ({
                          value: category.value,
                          text: t('Simulation.form.specialAdCategories.' + category.value),
                        })).sort((a, b) => a.text.localeCompare(b.text))}
                        placeholder={t('Simulation.form.specialAdCategory')}
                        classNames="simulation-form_sim-category-dropdown mb-3"
                        disabled={isPublished}
                      />
                    )}
                  </div>
                </div>

                <div className="requiredInputBox">
                  <Textarea
                    classNames="mb-0 mt-3 mt-md-0"
                    placeholder={getTranslation('Simulation.form.description')}
                    separatePlaceholder
                    value={description ?? ''}
                    name="description"
                    onChange={(e) => updateInput(e)}
                    maxLength={2500}
                    showMax
                    rows={5}
                    required
                  />
                </div>

                <div>
                  <Row className="mt-3">
                    <Col sm={12} md={5} lg={4} className="mb-3 mb-md-0">
                      <KeyFactsForm
                        key={`f-${0}`}
                        updateDate={updateDate}
                        fields={fields[0]}
                        updateInput={updateInput}
                        index={0}
                        removeField={removeField}
                      />
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>
          </div>

          {fields.length > 1 && (
            <div className="simulation-form__section simulation-form__key-facts">
              <Card className="card-default mb-0">
                <div className="card-header d-flex">
                  <h2 className="card-title-default">{t('Simulation.dateAndLocationFormTitle')}</h2>
                </div>

                <Card.Body className="pb-1">
                  <Row>
                    {fields.slice(1).map((f, idx) => (
                      <Col sm={12} md={6} lg={6} key={`f-${idx}`} className="mb-3">
                        <div className="d-flex align-items-center">
                          <KeyFactsForm
                            updateDate={updateDate}
                            fields={f}
                            updateInput={updateInput}
                            index={idx + 1}
                            removeField={removeField}
                            className="w-100"
                          />

                          <div className="minus-icon">
                            <img
                              src={minusIcon}
                              onClick={() => removeField(idx + 1)}
                              alt="removeIcon"
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            </div>
          )}

          {renderFormButtons ? (
            renderFormButtons(submitForm, onCancelClick, fields, loading, error)
          ) : (
            <FormButtons
              submitForm={submitForm}
              onCancelClick={onCancelClick}
              loading={loading}
              error={error}
            />
          )}
        </>
      </form>

      {showConfirmModal && (
        <PortalModal
          isOpen={showConfirmModal}
          onClickOutside={() => setShowConfirmModal(false)}
          wrapperClassName="event-editor_special-ad-confirm-modal"
          className=""
        >
          <SpecialAdConfirmModal
            onCancel={() => setShowConfirmModal(false)}
            onConfirm={() => {
              setShowConfirmModal(false)
              handleSubmit()
            }}
            loading={loading}
            specialAdCategory={specialAdCategory}
          />
        </PortalModal>
      )}
    </>
  )
}

EventEditorFormPrisma.propTypes = {
  event: PropTypes.object.isRequired,
  category: PropTypes.string.isRequired,
  postUpdatedEvent: PropTypes.func,
  renderFormButtons: PropTypes.func,
  onFormChanged: PropTypes.func,
  onCategoryChanged: PropTypes.func,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  isPublished: PropTypes.bool,
}

export default EventEditorFormPrisma
